function normalizeNonnegativeNumber(value) {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "") ||
    (typeof value !== "number" && typeof value !== "string")
  ) {
    return null;
  }

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return null;
  }

  return Math.max(number, 0);
}

function finiteResult(value) {
  return Number.isFinite(value) ? value : null;
}

export function calculateExpectedLossReduction(grossLoss, residualLoss) {
  const normalizedGrossLoss = normalizeNonnegativeNumber(grossLoss);
  const normalizedResidualLoss = normalizeNonnegativeNumber(residualLoss);

  if (normalizedGrossLoss === null || normalizedResidualLoss === null) {
    return null;
  }

  return finiteResult(Math.max(normalizedGrossLoss - normalizedResidualLoss, 0));
}

export function calculateResidualLoss(grossLoss, expectedLossReduction) {
  const normalizedGrossLoss = normalizeNonnegativeNumber(grossLoss);
  const normalizedReduction = normalizeNonnegativeNumber(expectedLossReduction);

  if (normalizedGrossLoss === null || normalizedReduction === null) {
    return null;
  }

  return finiteResult(Math.max(normalizedGrossLoss - normalizedReduction, 0));
}

export function calculateTransferredLoss(values) {
  if (values === null || typeof values !== "object") {
    return null;
  }

  const loss = normalizeNonnegativeNumber(values.loss);
  const deductible = normalizeNonnegativeNumber(values.deductible);
  const policyLimit = normalizeNonnegativeNumber(values.policyLimit);
  const coinsurance = normalizeNonnegativeNumber(values.coinsurance);

  if (
    loss === null ||
    deductible === null ||
    policyLimit === null ||
    coinsurance === null
  ) {
    return null;
  }

  const normalizedCoinsurance = Math.min(coinsurance, 1);
  const transferredLoss = Math.min(
    Math.max(loss - deductible, 0) * normalizedCoinsurance,
    policyLimit
  );

  return finiteResult(transferredLoss);
}

export function calculateRetainedLoss(loss, transferredLoss) {
  const normalizedLoss = normalizeNonnegativeNumber(loss);
  const normalizedTransferredLoss = normalizeNonnegativeNumber(transferredLoss);

  if (normalizedLoss === null || normalizedTransferredLoss === null) {
    return null;
  }

  return finiteResult(Math.max(normalizedLoss - normalizedTransferredLoss, 0));
}

export function calculateTotalRiskCost(values) {
  if (values === null || typeof values !== "object") {
    return null;
  }

  const mitigationCost = normalizeNonnegativeNumber(values.mitigationCost);
  const transferCost = normalizeNonnegativeNumber(values.transferCost);
  const retainedLoss = normalizeNonnegativeNumber(values.retainedLoss);

  if (
    mitigationCost === null ||
    transferCost === null ||
    retainedLoss === null
  ) {
    return null;
  }

  return finiteResult(mitigationCost + transferCost + retainedLoss);
}

export function calculateCoverageRatio(coveredScenarios, activeScenarios) {
  const covered = normalizeNonnegativeNumber(coveredScenarios);
  const active = normalizeNonnegativeNumber(activeScenarios);

  if (covered === null || active === null || active === 0) {
    return null;
  }

  return finiteResult(Math.min(Math.max(covered / active, 0), 1));
}
